/**
 * Q AI API Service Layer
 * Centralized API calls for Q AI functionality
 *
 * Endpoints:
 * - GET  /api/q/balance     - Token balance
 * - POST /api/q/ask         - Claude SSE streaming
 * - POST /api/q/auth/google - Google OAuth
 * - GET  /api/q/health      - Health check
 * - GET  /api/q/pricing     - Get pricing tiers & providers (F16)
 * - POST /api/q/topup       - Create payment checkout session (F16)
 */

import { baseURL } from '../config';

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Token pricing tiers for PricingModal
 */
export const PRICING_TIERS = [
  { id: 'starter', name: 'Starter', price: 10, tokens: 500, popular: false },
  { id: 'standard', name: 'Standard', price: 25, tokens: 1500, popular: true },
  { id: 'pro', name: 'Pro', price: 50, tokens: 5000, popular: false }
];

/**
 * Token costs per action type
 */
export const ACTION_COSTS = {
  chat: 5,     // Simple Q&A
  analyze: 10, // EA Analysis / Session Breakdown
  signal: 15   // Q Scan / Trading Signals
};

// ═══════════════════════════════════════════════════════════════════════════
// API FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get user's current token balance
 * @param {string} token - JWT token
 * @returns {Promise<{balance: number, isLow: boolean, totalUsed: number}>}
 */
export const getBalance = async (token) => {
  const response = await fetch(`${baseURL}/api/q/balance`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error(`Failed to get balance: ${response.status}`);
  }

  const data = await response.json();
  return {
    balance: data.balance || 0,
    isLow: (data.balance || 0) <= 15,
    totalUsed: data.totalUsed || 0
  };
};

/**
 * Send message to Q AI with SSE streaming
 * @param {string} token - JWT token
 * @param {string} message - User message
 * @param {string} actionType - 'chat' | 'analyze' | 'signal'
 * @param {function} onToken - Callback for each streamed token
 * @param {function} onDone - Callback when stream completes with new balance
 * @param {function} onError - Callback for errors
 * @returns {AbortController} - For canceling the request
 */
export const askQAI = (token, message, actionType, onToken, onDone, onError) => {
  const controller = new AbortController();

  const fetchSSE = async () => {
    try {
      const response = await fetch(`${baseURL}/api/q/ask`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream'
        },
        body: JSON.stringify({ message, actionType }),
        signal: controller.signal
      });

      // Handle 402 Payment Required (insufficient tokens)
      if (response.status === 402) {
        onError({ code: 402, message: 'Insufficient tokens' });
        return;
      }

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6).trim();
            if (jsonStr === '[DONE]') {
              continue;
            }

            try {
              const data = JSON.parse(jsonStr);

              if (data.token) {
                onToken(data.token);
              }

              if (data.done) {
                onDone(data.newBalance);
                return;
              }

              if (data.error) {
                onError({ code: 500, message: data.error });
                return;
              }
            } catch (e) {
              // Skip malformed JSON
            }
          }
        }
      }

      // Stream ended without explicit done
      onDone(null);
    } catch (error) {
      if (error.name === 'AbortError') {
        // Request was cancelled, don't call onError
        return;
      }
      onError({ code: 500, message: error.message });
    }
  };

  fetchSSE();
  return controller;
};

/**
 * Login with Google OAuth
 * @param {string} googleCredential - Google ID token from OAuth
 * @param {string} source - 'qai' for standalone, 'platform' for QTradeView
 * @returns {Promise<{token: string, user: object}>}
 */
export const googleLogin = async (googleCredential, source = 'qai') => {
  const response = await fetch(`${baseURL}/api/q/auth/google`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      credential: googleCredential,
      source
    })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Login failed: ${response.status}`);
  }

  return response.json();
};

/**
 * Check Q AI service health
 * @returns {Promise<{status: string, claudeConfigured: boolean, timestamp: string}>}
 */
export const checkHealth = async () => {
  const response = await fetch(`${baseURL}/api/q/health`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error(`Health check failed: ${response.status}`);
  }

  return response.json();
};

/**
 * Get cost for specific action type
 * @param {string} actionType - 'chat' | 'analyze' | 'signal'
 * @returns {number} - Token cost
 */
export const getActionCost = (actionType) => {
  return ACTION_COSTS[actionType] || ACTION_COSTS.chat;
};

// ═══════════════════════════════════════════════════════════════════════════
// QBRIDGE API FUNCTIONS (E9)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * QBridge download info - fallback values if API unavailable
 * NOTE: URL should be updated when actual CDN/hosting is configured
 */
const QBRIDGE_FALLBACK = {
  url: null, // Set to null until real download URL is available
  version: '1.0.0',
  size: '15 MB',
  checksum: '',
  releaseNotes: 'Initial release',
  requirements: {
    os: 'Windows 10/11',
    mt5: 'MetaTrader 5 (any broker)'
  },
  available: false // Flag to indicate download not yet available
};

/**
 * Get QBridge download information
 * @returns {Promise<{url: string, version: string, size: string, checksum: string, releaseNotes: string, requirements: object}>}
 */
export const getQBridgeDownload = async () => {
  try {
    const response = await fetch(`${baseURL}/api/q/qbridge/download`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      console.warn('QBridge download API unavailable, using fallback');
      return QBRIDGE_FALLBACK;
    }

    return response.json();
  } catch (error) {
    console.warn('Failed to fetch QBridge download info:', error);
    return QBRIDGE_FALLBACK;
  }
};

/**
 * Get QBridge sync status for current user
 * @param {string} token - JWT token
 * @returns {Promise<{hasSynced: boolean, lastSyncAt: string|null, tradeCount: number, accountInfo: object|null}>}
 */
export const getSyncStatus = async (token) => {
  try {
    const response = await fetch(`${baseURL}/api/q/sync-status`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized');
      }
      // Return default values if endpoint not available yet
      return {
        hasSynced: false,
        lastSyncAt: null,
        tradeCount: 0,
        accountInfo: null
      };
    }

    return response.json();
  } catch (error) {
    console.warn('Failed to fetch sync status:', error);
    return {
      hasSynced: false,
      lastSyncAt: null,
      tradeCount: 0,
      accountInfo: null
    };
  }
};

/**
 * Check if QBridge connection is active
 * @param {string} token - JWT token
 * @returns {Promise<boolean>}
 */
export const checkQBridgeConnection = async (token) => {
  try {
    const status = await getSyncStatus(token);
    return status.hasSynced === true;
  } catch {
    return false;
  }
};

/**
 * Get user's synced trades from QBridge (E4)
 * @param {string} token - JWT token
 * @param {object} options - Query options
 * @param {number} options.limit - Max trades to return (default: 50)
 * @param {number} options.offset - Pagination offset (default: 0)
 * @param {string} options.sortBy - Sort field (default: 'closeTime')
 * @param {string} options.sortOrder - 'asc' | 'desc' (default: 'desc')
 * @returns {Promise<{trades: Array, total: number, hasMore: boolean}>}
 */
export const getUserTrades = async (token, options = {}) => {
  const { limit = 50, offset = 0, sortBy = 'closeTime', sortOrder = 'desc' } = options;

  try {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      sortBy,
      sortOrder
    });

    const response = await fetch(`${baseURL}/api/q/trades?${params}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized');
      }
      // Return empty if endpoint not available
      return { trades: [], total: 0, hasMore: false };
    }

    const data = await response.json();
    return {
      trades: data.trades || data.data || [],
      total: data.total || 0,
      hasMore: data.hasMore || false
    };
  } catch (error) {
    console.warn('Failed to fetch user trades:', error);
    return { trades: [], total: 0, hasMore: false };
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// PAYMENT API FUNCTIONS (F16)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get pricing tiers and available payment providers
 * @returns {Promise<{tiers: Array, availableProviders: Array}>}
 */
export const getQAIPricing = async () => {
  try {
    const response = await fetch(`${baseURL}/api/q/pricing`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to get pricing: ${response.status}`);
    }

    const result = await response.json();
    return result.data || { tiers: PRICING_TIERS, availableProviders: [] };
  } catch (error) {
    console.warn('Failed to fetch pricing, using fallback:', error);
    // Fallback to local constants if API unavailable
    return { tiers: PRICING_TIERS, availableProviders: [] };
  }
};

/**
 * Create payment checkout session
 * @param {string} token - JWT token
 * @param {string} tierId - 'starter' | 'standard' | 'pro'
 * @param {string} provider - 'payoneer' | 'coinbase'
 * @param {string} returnUrl - URL to redirect after successful payment
 * @param {string} cancelUrl - URL to redirect if payment cancelled
 * @returns {Promise<{success: boolean, checkoutUrl: string, sessionId: string}>}
 */
export const createCheckout = async (token, tierId, provider, returnUrl, cancelUrl) => {
  const response = await fetch(`${baseURL}/api/q/topup`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      tierId,
      provider,
      returnUrl: returnUrl || `${window.location.origin}/q-ai/coach?payment=success`,
      cancelUrl: cancelUrl || `${window.location.origin}/q-ai/coach?payment=cancelled`
    })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.message || `Checkout failed: ${response.status}`);
  }

  return result.data || result;
};
